import { Component } from '@angular/core';
import { IonicPage, ModalController, NavController, NavParams, Platform, ToastController } from 'ionic-angular';
import { OperationCommentsPage } from '../operation-comments/operation-comments';
import { OperationService } from '../../../services/operations/operation/operation.service';
import {
  DailyLogOperationInstance,
  DailyLogType,
  DailyOperationLogRecord,
  LogComment,
  OperationType,
  ProjectSpecificOperationSummary,
  ResponsibilityType,
  StepInstance,
} from '../../../models/projects/operation.models';
import { DailyLogModalPage } from '../../daily-log/daily-log-modal/daily-log-modal';
import { DateService } from '../../../services/date/date.service';
import { OperationTimeService } from '../../../services/operations/operation-time/operation-time.service';
import { StepCommentsPage } from '../step-comments/step-comments';
import { OperationGenericCommentsPage } from '../operation-generic-comments/operation-generic-comments';
import { OperationValidatorService } from '../../../services/operations/operation-validator.service';
import { ActionType, ErrorType, ValidationResult } from '../../../models/projects/validation.models';
import { ErrorsComponent } from '../../shared/components/errors/errors.component';
import { NavHelperService } from '../../../services/nav/nav-helper.service';
import { RemoveCommentComponent } from '../remove-comment/remove-comment.component';
import { DailyLogService } from '../../../services/daily-log/daily-log.service';
import { DateParserService } from '../../../services/daily-log/daily-log-time/date-parser-service';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { MultipleDayService } from '../../../services/mutiple-day/multiple-day.service';
import { cloneDeep } from 'lodash';
import { RecordOperationLinkService } from '../../../services/record-operation-link/record-operation-link.service';
import { StepService } from '../../../services/step/step.service';
import { DailyLogTimeService } from '../../../services/daily-log/daily-log-time/daily-log-time-service';
import { AffectedOperationsPage } from '../affected-operations/affected-operations';
import { OperationSaveService } from '../../../services/operations/operation-save/operation-save.service';
import { SetupService } from '../../../services/setup/setup.service';
import { OperationSplitPage } from '../operation-split/operation-split';
import { OperationDetailsEditLocationSelectPage } from '../operation-details-edit-location-select/operation-details-edit-location-select';
import { AppData, AppDataSummary, OperationInstanceSaveRequest } from '../../../models/app-data/app-data';
import { AppDataSyncService } from '../../../services/app-data-sync/app-data-sync.service';
import { LocalOperationSaveService } from '../../../services/local/operation/local-operation-save.service';
import { DailyLogEntriesService } from '../../../services/daily-log/daily-log-entries/daily-log-entries.service';
import { TimeZoneService } from '../../../services/time-zone/time-zone.service';
import { JackingActivityService } from '../../../services/jacking-activity/jacking-activity.service';
import { FieldPositioningService } from '../../../services/field-positioning/field-positioning.service';
import { OnSiteMovementService } from '../../../services/on-site-movement/on-site-movement.service';
import { DailyLogValidationService } from '../../../services/daily-log/daily-log-validation/daily-log-validation.service';
import { LockedDayPermissions } from '../../../models/permissions/permission.models';
import { PermissionsService } from '../../../services/permissions/permissions.service';
import { CompleteDayService } from '../../../services/complete-day/complete-day.service';
import { AppConfig } from '../../../app/app.config';
import { DateTime } from 'luxon';
import { DailyLogPage } from '../../daily-log/daily-log/daily-log';
import { NavMainMenuPage } from '../../nav/nav-menus/nav-main-menu/nav-main-menu';
import { animate, sequence, style, transition, trigger } from '@angular/animations';
import { SupportContextKey } from '../../../models/support/support.models';
import { SupportContextService } from '../../../services/support/support-context.service';

@IonicPage()
@Component({
  selector: 'page-operation-details-edit',
  templateUrl: 'operation-details-edit.html',
  animations: [
    trigger('excludeSplitStep', [
      transition(':leave', [
        style({ height: '*', opacity: '1', overflow: 'hidden' }),
        sequence([
          animate('400ms ease-in', style({ height: '0', opacity: '0' })),
          animate('400ms ease-in', style({ margin: '0' })),
        ])
      ])
    ])
  ]
})
export class OperationDetailsEditPage {
  config = AppConfig.settings;
  operation: ProjectSpecificOperationSummary = new ProjectSpecificOperationSummary();
  operationInstance: DailyLogOperationInstance = new DailyLogOperationInstance();
  originalInstanceCopy: DailyLogOperationInstance = new DailyLogOperationInstance();
  saved: boolean = false;
  validationResult: ValidationResult = new ValidationResult();
  saveValidationIsValid: boolean = true;
  private operationValidationSettings: ProjectSpecificOperationSummary;
  responsibilityType = ResponsibilityType;
  responsibilitySelectOptions = {cssClass: 'responsibility-select-popover'};
  responsibilityValidationAttempted: boolean = false;
  mostRecentTime: PtLuxonDate;
  originalComments: LogComment[];
  popFn = null;
  isWeb: boolean;
  logs: DailyOperationLogRecord[] = [];
  lastStepPosition: number = null;
  loading: boolean = false;
  badgesLoaded: boolean = false;
  swapMessage = '';
  canConfirmOperationTimes: boolean;
  public mask: Array<string | RegExp> = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];
  firstLoadComplete: boolean;
  animationPaused: boolean = false;

  dailyLogDate: PtLuxonDate;
  endOfDay: PtLuxonDate;

  min: DateTime = DateTime.local();
  max: DateTime = DateTime.local();

  multipleDaysExist: boolean;

  lockedDayPermissions: LockedDayPermissions;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public platform: Platform,
    public operationService: OperationService,
    public operationTimeService: OperationTimeService,
    public operationValidatorService: OperationValidatorService,
    public dateService: DateService,
    public dateParserService: DateParserService,
    public dailyLogService: DailyLogService,
    public modalController: ModalController,
    public navHelperService: NavHelperService,
    public multipleDayService: MultipleDayService,
    public stepService: StepService,
    public setupService: SetupService,
    public operationSaveService: OperationSaveService,
    public appDataSyncService: AppDataSyncService,
    private localOperationSaveService: LocalOperationSaveService,
    private dailyLogEntriesService: DailyLogEntriesService,
    private toastCtrl: ToastController,
    private timeZoneService: TimeZoneService,
    private fieldPositioningService: FieldPositioningService,
    private jackingActivitySerivce: JackingActivityService,
    private onSiteMovementService: OnSiteMovementService,
    private dailyLogValidationService: DailyLogValidationService,
    private permissionService: PermissionsService,
    private supportContextService: SupportContextService,
  ) {
  }

  async ionViewDidEnter() {
    // we want to call initViewLoad() every time the view loads, and initFirstLoad() only the first time
    // these used to be: initFirstLoad() == ionViewDidLoad() and initViewEnter() == ionViewDidEnter(), but we needed to replace the ionic lifecycle
    // events to ensure that ionViewDidEnter() always runs only after ionViewDidLoad() (was causing null reference sometimes otherwise)
    if (!this.firstLoadComplete) {
      await this.initFirstLoad();
      await this.initViewEnter();
      this.firstLoadComplete = true;
    } else {
      await this.initViewEnter();
    }

    await this.refreshOperationValidationSettings();

    this.setSupportContext();
    this.originalInstanceCopy = cloneDeep(this.operationInstance); // need a copy to reliably fix affected operation pause times when editing a delay
  }

  async initFirstLoad() {
    this.loading = true;
    this.isWeb = this.platform.is('core');
    this.operationInstance = cloneDeep(this.navParams.get('instance'));
    this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();
    this.lockedDayPermissions = await this.permissionService.lockedDayPermissions(this.dailyLogDate);

    const appData = new AppData();
    appData.dailyLogOperationInstances = [this.operationInstance];

    await this.appDataSyncService.refreshTimesToShow(appData);

    if (this.config.isImplementerEnvironment) {
      this.endOfDay = await this.dateService.getEndOfDayLocally(this.dailyLogDate.dateTime);
      await this.operationTimeService.setLockedDayDisabledFlags(this.operationInstance, this.lockedDayPermissions, this.endOfDay);
    }

    this.multipleDaysExist = await this.operationValidatorService.multipleDaysExist(this.operationInstance);

    this.operation = this.operationInstance.projectSpecificOperation;
    this.operationInstance.projectSpecificOperation = this.operation;
    this.operationInstance.projectSpecificOperationId = this.operation.id;

    this.logs = await this.appDataSyncService.getLogsForOperation(this.operationInstance.id);

    this.navHelperService.unsavedChanges = false;

    this.mostRecentTime = cloneDeep(this.operationInstance.startTime);
    this.originalComments = cloneDeep(this.operationInstance.comments);

    this.saved = false;

    this.min = this.dailyLogDate.dateTime;
    this.max = this.lockedDayPermissions.functionalPermissions.canImpactFutureDay
      ? !this.operationInstance.startTime.dateTime
        ? this.min
        : await this.dateService.addDuration(this.dailyLogDate.dateTime, {day: 1}, true)
      : cloneDeep(this.dailyLogDate.dateTime) as DateTime;

    // setting confirmed from logs - doesn't make much sense but i believe it was originally put in so that if the user quickly confirmed a time
    // then immediately went to edit the operation it would show as confirmed (where it may still actually be in the process of saving on the API)
    // i'm not sure if this is required any more as the edit buttons get disabled while it saves time changes/confirms now, but leaving it in
    this.setConfirmedFromLogs(this.operationInstance);
    await this.checkOverlaps();

    this.canConfirmOperationTimes = !this.jackingActivitySerivce.operationRequiresJackingActivity(this.operationInstance)
      && !this.onSiteMovementService.operationRequiresOnSiteMovementRecord(this.operationInstance);

    // TODO-RYAN: Put this back in once SOS-1080/SOS-1081 are done
    // !this.jackingActivityService.operationRequiresJackingActivity(this.operationInstance);

    if (this.operationInstance.stepCollectionInstance) {
      for (const step of this.operationInstance.stepCollectionInstance.stepInstances) {
        step.canConfirmTimes = this.canConfirmStepTime(step);
      }
    }

    this.loading = false;
  }

  async initViewEnter() {
    this.navHelperService.warned = false;
    this.navHelperService.showWarning = false;
    this.navHelperService.triedToSave = false;

    for (let i = 0; i < this.operationInstance.switchTimes.length; i++) {
      const element = this.operationInstance.switchTimes[i];
      await this.stepService.splitStepsAtTime(this.operationInstance, element.date);
    }

    this.swapMessage = this.operationInstance.isConcurrentOperation
      ? 'Change to contractual'
      : 'Change to concurrent';

    await this.stepService.attemptJoinStepsInOperation(this.operationInstance);

    this.popFn = this.navCtrl.pop;
    this.navCtrl.pop = (): Promise<any> => {
      this.navHelperService.triedToSave = true;

      if (this.navHelperService.warned || this.navHelperService.unsavedChanges === false) {
        this.navCtrl.pop = this.popFn;
        this.navCtrl.pop();
        return;
      }

      this.navHelperService.showWarning = true;
      this.navHelperService.warned = true;

      this.navCtrl.pop = this.popFn;
      return;
    };

    this.setTypes();
  }

  ionViewDidLeave() {
    this.navHelperService.warned = false;
    this.navHelperService.showWarning = false;
    this.navHelperService.unsavedChanges = false;
    this.navCtrl.pop = this.popFn;
  }

  openDailyLogModal() {
    const dailyLogModal = this.modalController.create(DailyLogModalPage);
    dailyLogModal.present();
  }

  openErrorModal() {
    const errorModal = this.modalController.create(ErrorsComponent, {
      validationResult: this.validationResult,
      refresh: true
    });
    errorModal.present();
  }

  openRemoveCommentModal() {
    const removeCommentModal = this.modalController.create(RemoveCommentComponent, {
      comment: this.operationInstance.stepCollectionInstance.stepInstances[this.lastStepPosition].comment.data,
      message: 'The below comment is attached to the step you are about to remove. Do you want to '
        + 'remove the comment as well or keep the comment against the operation?',
      trueText: 'Remove Comment',
      falseText: 'Keep Comment'
    }, {
      cssClass: 'modal-remove-comment'
    });

    removeCommentModal.onDidDismiss(data => {

      const step = this.operationInstance.stepCollectionInstance.stepInstances[this.lastStepPosition];

      if (!data.choice) {
        const logComment = new LogComment();
        logComment.startTimeToShow = step.startTimeToShow;

        const comment = new LogComment();
        comment.data = step.comment.data;
        comment.appear = step.comment.appear;
        comment.milestone = step.comment.milestone;
        logComment.data = comment.data;
        logComment.milestone = comment.milestone;
        logComment.startTimeToShow = step.startTimeToShow;
        logComment.startTimeValid = step.startTimeToShow != null;
        logComment.startTimeDate = step.startTime;
        logComment.startTimeOffset = step.startTimeOffset;

        if (!this.operationInstance.comments) {
          this.operationInstance.comments = [];
        }

        this.operationInstance.comments.push(logComment);
      }

      step.comment = null;

      this.toggleExclude(step, this.lastStepPosition);
    });

    removeCommentModal.present();
  }

  async openEditLocationSelect() {
    if (!await this.validateResponsibilityForSave()) {
      return;
    }

    this.navCtrl.push(OperationDetailsEditLocationSelectPage,
      {
        instance: this.operationInstance,
        stepInstances: this.operationInstance.stepCollectionInstance
          ? this.operationInstance.stepCollectionInstance.stepInstances
          : null,
        originalComments: this.originalComments,
        dailyLogDate: this.dailyLogDate
      });
  }

  goToGenericOperationComments() {
    this.navHelperService.unsavedChanges = true;
    this.navCtrl.push(OperationGenericCommentsPage, {
      operationInstance: this.operationInstance,
      dailyLogDate: this.dailyLogDate,
      lockedDayPermissions: this.lockedDayPermissions
    });
  }

  goToOperationStartComment() {
    this.navHelperService.unsavedChanges = true;
    this.navCtrl.push(OperationCommentsPage, {
      operationInstance: this.operationInstance,
      isStart: true,
      lockedDayPermissions: this.lockedDayPermissions
    });
  }

  goToOperationEndComment() {
    this.navHelperService.unsavedChanges = true;
    this.navCtrl.push(OperationCommentsPage, {
      operationInstance: this.operationInstance,
      isStart: false,
      lockedDayPermissions: this.lockedDayPermissions
    });
  }

  goToOperationSplitPage() {
    this.navCtrl.push(OperationSplitPage, {
      instance: this.operationInstance,
      dailyLogDate: this.dailyLogDate.dateTime,
      callback: this.navHelperService.dataSavedCallback
    });
  }

  goToStepComment(stepInstance?: StepInstance) {
    this.navHelperService.unsavedChanges = true;
    this.navCtrl.push(StepCommentsPage, {
      stepInstance: stepInstance,
      lockedDayPermissions: this.lockedDayPermissions
    });
  }

  toggleStartConfirmStep(stepInstance: StepInstance) {
    if (stepInstance.canConfirmTimes) {
      this.navHelperService.unsavedChanges = true;
      stepInstance.startConfirmed = !stepInstance.startConfirmed;
    }
  }

  toggleEndConfirmStep(stepInstance: StepInstance) {
    if (stepInstance.canConfirmTimes) {
      this.navHelperService.unsavedChanges = true;
      stepInstance.endConfirmed = !stepInstance.endConfirmed;
    }
  }

  toggleStartConfirmOp(operationInstance: DailyLogOperationInstance) {
    if (this.canConfirmOperationTimes) {
      this.navHelperService.unsavedChanges = true;
      operationInstance.startConfirmed = !operationInstance.startConfirmed;
    }
  }

  toggleEndConfirmOp(operationInstance: DailyLogOperationInstance) {
    if (this.canConfirmOperationTimes) {
      this.navHelperService.unsavedChanges = true;
      operationInstance.endConfirmed = !operationInstance.endConfirmed;
    }
  }

  async toggleExclude(stepInstance: StepInstance, position: number) {
    const otherGroupedSteps = this.operationInstance.stepCollectionInstance.stepInstances
      .filter(s => s.group === stepInstance.group && s.number < stepInstance.number && s.id !== stepInstance.id);

    if (otherGroupedSteps != null && otherGroupedSteps.length > 0) {
      // if there are other steps filtered above, then we are excluding a step that was split from a single step
      // so we just want to delete the split part of this group
      this.operationInstance.stepCollectionInstance.stepInstances.splice(position, 1);
      this.operationInstance = await this.operationTimeService
        .updateFutureSteps(position, otherGroupedSteps[otherGroupedSteps.length - 1].endTime, this.operationInstance,
          this.operationInstance.stepCollectionInstance.stepInstances,
          false, this.dailyLogDate.dateTime);
      return;
    }

    if (!!stepInstance.comment) {
      this.lastStepPosition = position;
      this.openRemoveCommentModal();
      return;
    }

    position++; // need to add 1 for the below adjustAllEstimates() to work (with regard to the bug where stepDelay is increased when editing a time - SOS-1561)

    this.navHelperService.unsavedChanges = true;
    stepInstance.exclude = !stepInstance.exclude;

    stepInstance.startConfirmed = false;
    stepInstance.endConfirmed = false;

    const latestTime = this.operationInstance.startTime;

    // await this.multipleDayService.adjustDays(this.operationInstance, null, null, null, this.dailyLogDate.dateTime);

    if (!this.config.isImplementerEnvironment) {
      this.operationInstance = await this.operationTimeService
        .updateFutureSteps(0, latestTime, this.operationInstance,
          this.operationInstance.stepCollectionInstance.stepInstances,
          false, this.dailyLogDate.dateTime);

      await this.multipleDayService.workOutEndDate(this.operationInstance, null, this.dailyLogDate.dateTime);

      this.operationTimeService.adjustAllEstimates(this.operationInstance, position);
    }

    this.multipleDaysExist = await this.operationValidatorService.multipleDaysExist(this.operationInstance);

    await this.validateOperation();

    this.setTypes();
  }

  async startChanged(position: number, stepInstance?: StepInstance, datePicker?: boolean, dateTime?: DateTime) {
    let allowed = false;

    if (!stepInstance) {
      // if user previously nulled the time and is now re-entering one, then startTime.dateTime will be null,
      // the process of updating future step times wont work until they also select a date
      if (this.operationInstance.startTime && !this.operationInstance.startTime.dateTime) {
        // need the || here to ensure the process works as intended whichever way the user fills out the step, either datepicker or time
        // input first
        this.operationInstance.startTime.dateTime = dateTime || this.operationInstance.startTime.dateTime;
      }

      allowed = await this.dateService.timeAllowed(
        this.operationInstance.startTimeToShow,
        this.operationInstance.startTime && this.operationInstance.startTime.dateTime ? this.operationInstance.startTime.dateTime : this.dailyLogDate.dateTime
      );
    } else {
      if (datePicker && !dateTime) {
        stepInstance.startTime.dateTime = null;
        stepInstance.startTimeToShow = null;
      }

      if (stepInstance.startTime && !stepInstance.startTime.dateTime) {
        stepInstance.startTime.dateTime = dateTime || stepInstance.startTime.dateTime;

        // if user has entered the time but not picked a date in the datepicker (when they are adding an end to a previously null time)
        if (this.dateService.timeIsValid(stepInstance.startTimeToShow)) {
          stepInstance.startTime.dateTime = this.dailyLogDate.dateTime;
        }
      }

      allowed = await this.dateService.timeAllowed(stepInstance.startTimeToShow, stepInstance.startTime.getDate());
    }

    if (!allowed) {
      return;
    }

    const startTimeCopy = cloneDeep(this.operationInstance.startTime);

    this.navHelperService.unsavedChanges = true;

    let latestTime = cloneDeep(this.operationInstance.startTime);

    // operation start time changed
    if (!stepInstance) {
      this.operationInstance.startTime = await this.dateService.setPtLuxonDateFromUserEnteredString(
        this.operationInstance.startTimeToShow,
        this.operationInstance.startTime && this.operationInstance.startTime.dateTime ? this.operationInstance.startTime.dateTime : this.dailyLogDate.dateTime);

      if (this.canConfirmOperationTimes) {
        this.operationInstance.startConfirmed = true;
      }

      if (this.operationInstance.startTime.dateTime > this.operationInstance.cancelledTime) {
        this.operationInstance.cancelledTime = null;
      }

      if (this.operationInstance.startTime.dateTime == null) {
        await this.validateOperation();
        this.operationTimeService.adjustAllEstimates(this.operationInstance, position);
        await this.calculateOperationOffsets(this.operationInstance);
        this.setTypes();
      }

      latestTime = this.operationInstance.startTime;
      if (this.canConfirmOperationTimes) {
        this.operationInstance.startConfirmed = true;
      }
    } else {
      if (stepInstance.canConfirmTimes) {
        stepInstance.startConfirmed = true;
      }

      const previousTime = this.operationService
        .getPreviousTimeFromSteps(this.operationInstance.stepCollectionInstance, stepInstance) || this.operationInstance.startTime.dateTime;

      if (previousTime && previousTime.toMillis() < this.dailyLogDate.dateTime.toMillis()) {
        latestTime.date = this.dailyLogDate.dateTime;
      } else {
        latestTime.date = previousTime;
      }

      const chosenDate = stepInstance.startTime.dateTime || previousTime;
      // set date based on user time input
      stepInstance.startTime = await this.dateService
        .setPtLuxonDateFromUserEnteredString(stepInstance.startTimeToShow,
          datePicker
            ? dateTime
            : (chosenDate && chosenDate.toMillis() >= this.dailyLogDate.dateTime.toMillis())
            ? chosenDate
            : this.dailyLogDate.dateTime);

      latestTime = stepInstance.startTime;

      // operation set open ended
      if (stepInstance.startTime.dateTime == null) {
        this.operationTimeService
          .removeTime(this.operationInstance.stepCollectionInstance.stepInstances, this.operationInstance, stepInstance, position);
      }

      if (stepInstance) {
        position++;
      }

      // if time is smaller, entered date is assumed to be on the next day
      if (!datePicker && stepInstance.startTime.dateTime && stepInstance.startTime.dateTime.toMillis() < previousTime.toMillis()) {
        stepInstance.startTime = await this.dateService.incrementDay(stepInstance.startTime);
      }
    }

    // ensures that when changing a step time to be before a previous time, that the date gets changed on the step to be on the next day
    // await this.multipleDayService.adjustDays(this.operationInstance, position, datePicker, null, this.dailyLogDate.dateTime);

    // only update future times if we know the offset for the time entered - if we don't then these will be updated when the user selects the offset
    if (!this.config.isImplementerEnvironment) {
      if ((stepInstance
        ? (stepInstance.startTime.dateTime && stepInstance.startTime.offsetNotNull() || !stepInstance.startTime.dateTime) // if user nulls step we still want to run the below
        : this.operationInstance.startTime)
        && this.operationInstance.startTime.dateTime && this.operationInstance.startTime.offsetNotNull()) {

        if (!!this.operationInstance.stepCollectionInstance) {
          this.operationInstance = await this.operationTimeService
            .updateFutureSteps(position,
              latestTime,
              this.operationInstance,
              this.operationInstance.stepCollectionInstance.stepInstances,
              true,
              null,
              stepInstance ? stepInstance.canConfirmTimes : null);
        }

        await this.multipleDayService.workOutEndDate(this.operationInstance, null, null, null, startTimeCopy);
        this.operationTimeService.adjustAllEstimates(this.operationInstance, position);

        this.setTypes();
      }
    } else if (stepInstance && stepInstance.secondsSpan === 0) {
      // need to keep point in time step end time together if we are in implementer mode, as we have disabled the step time automation logic above
      stepInstance.endTime = cloneDeep(stepInstance.startTime);
    }

    await this.validateOperation(latestTime);
  }

  async endChanged(position: number, stepInstance?: StepInstance, datePicker?: boolean, dateTime?: DateTime) {
    let allowed = false;

    if (!stepInstance) {
      if (datePicker && !dateTime) {
        this.operationInstance.endTime.dateTime = null;
        this.operationInstance.endTimeToShow = null;
      }

      if (this.operationInstance.endTime && !this.operationInstance.endTime.dateTime) {
        // need the || here to ensure the process works as intended whichever way the user fills out the step, either datepicker or time
        // input first
        this.operationInstance.endTime.dateTime = dateTime || this.operationInstance.endTime.dateTime;
      }

      allowed = await this.dateService.timeAllowed(
        this.operationInstance.endTimeToShow,
        this.operationInstance.endTime && this.operationInstance.endTime.dateTime ? this.operationInstance.endTime.dateTime : this.dailyLogDate.dateTime
      );
    } else {
      if (datePicker && !dateTime) {
        stepInstance.endTime.dateTime = null;
        stepInstance.endTimeToShow = null;
      }

      if (stepInstance.endTime && !stepInstance.endTime.dateTime) {
      // need the || here to ensure the process works as intended whichever way the user fills out the step, either datepicker or time
      // input first
      stepInstance.endTime.dateTime = dateTime || stepInstance.endTime.dateTime;

        // if user has entered the time but not picked a date in the datepicker (when they are adding an end to a previously null time)
        if (this.dateService.timeIsValid(stepInstance.endTimeToShow)) {
          stepInstance.endTime.dateTime = this.dailyLogDate.dateTime;
        }
      }

      allowed = await this.dateService.timeAllowed(stepInstance.endTimeToShow, stepInstance.endTime.getDate());
    }

    if (!allowed) {
      return;
    }

    const startTimeCopy = cloneDeep(this.operationInstance.startTime);
    this.operationInstance.endTime = await this.dateService.setPtLuxonDateFromUserEnteredString(
      this.operationInstance.endTimeToShow,
      datePicker
        ? dateTime
        : this.operationInstance.endTime && this.operationInstance.endTime.dateTime
          ? this.operationInstance.endTime.dateTime
          : this.dailyLogDate.dateTime);

    this.navHelperService.unsavedChanges = true;

    let latestTime = this.operationInstance.startTime;

    // operation end time changed
    if (!stepInstance) {
      if (this.canConfirmOperationTimes) {
        this.operationInstance.endConfirmed = true;
      }
    } else {
      const previousTime = this.operationService
        .getPreviousTimeFromSteps(this.operationInstance.stepCollectionInstance, stepInstance) || this.operationInstance.startTime.dateTime;

      // set date based on user time input
      stepInstance.endTime = await this.dateService
        .setPtLuxonDateFromUserEnteredString(stepInstance.endTimeToShow,
          datePicker // if datepicker, then need to overwrite the endTime date with whatever the user has selected
            ? dateTime
            : stepInstance.endTime.dateTime
            ? stepInstance.endTime.dateTime
            : this.operationInstance.endTime && this.operationInstance.endTime.dateTime
              ? this.operationInstance.endTime.dateTime
              : this.dailyLogDate.dateTime || previousTime);

      // operation set open ended
      if (!stepInstance.endTime || !stepInstance.endTime.getDate()) {
        this.operationTimeService
          .removeTime(this.operationInstance.stepCollectionInstance.stepInstances, this.operationInstance, stepInstance, position);

        // if time is smaller, entered date is assumed to be on the next day
        if (!datePicker && previousTime && this.operationInstance.endTime && this.operationInstance.endTime.dateTime
          && this.operationInstance.endTime.dateTime.toMillis() < previousTime.toMillis()) {
          stepInstance.endTime = await this.dateService.incrementDay(stepInstance.endTime);
        }
      }

      // if time is smaller, entered date is assumed to be on the next day
      if (!datePicker && stepInstance.endTime.dateTime && stepInstance.endTime.dateTime.toMillis() < previousTime.toMillis()) {
        stepInstance.endTime = await this.dateService.incrementDay(stepInstance.endTime);
      }

      if (stepInstance.canConfirmTimes) {
        stepInstance.endConfirmed = true;
      }

      position++;

      latestTime = stepInstance.endTime;
    }

    // only update future times if we know the offset for the time entered - if we don't then these will be updated when the user selects the offset
    if ((stepInstance
      ? (stepInstance.endTime.dateTime && stepInstance.endTime.offsetNotNull() || !stepInstance.endTime.dateTime) // if user nulls step we still want to run the below
      : (this.operationInstance.endTime.dateTime && this.operationInstance.endTime.offsetNotNull() || !this.operationInstance.endTime.dateTime))) {

      if (this.operationInstance.stepCollectionInstance && stepInstance) {
        this.operationInstance = await this.operationTimeService
          .updateFutureSteps(position,
            latestTime,
            this.operationInstance,
            this.operationInstance.stepCollectionInstance.stepInstances,
            false);
      }

      await this.multipleDayService.workOutEndDate(this.operationInstance, true, this.dailyLogDate.dateTime, null, startTimeCopy, true, !stepInstance);
      this.operationTimeService.adjustAllEstimates(this.operationInstance, position);

      this.setTypes();
    }

    await this.validateOperation();
  }

  /* Gets called only once when the value is complete, as opposed to startChange() which gets called on each character entered */
  async startValueChanged(position: number, stepInstance?: StepInstance) {
    let valid = false;
    if (!stepInstance) {
      valid = this.dateService.timeIsValid(this.operationInstance.startTimeToShow);
    } else {
      valid = this.dateService.timeIsValid(stepInstance.startTimeToShow);
    }

    if (!valid) {
      if (!stepInstance) {
        this.operationInstance.startTimeToShow
          = this.dateService.setTimeStringFromPtLuxonDate(this.operationInstance.startTime);
      } else {
        // this.operationInstance.stepCollectionInstance.stepInstances[position].startTimeToShow
        // = this.dateService.setTimeStringFromPtLuxonDate(this.operationInstance.stepCollectionInstance.stepInstances[position].startTime.dateTime);
      }
    }
  }

  /* Gets called only once when the value is complete, as opposed to endChange() which gets called on each character entered */
  async endValueChanged(position: number, stepInstance?: StepInstance) {
    let valid = false;

    if (!stepInstance) {
      valid = this.dateService.timeIsValid(this.operationInstance.endTimeToShow);
    } else {
      valid = this.dateService.timeIsValid(stepInstance.endTimeToShow);
    }

    if (!valid) {
      if (!stepInstance) {
        this.operationInstance.endTimeToShow
          = this.dateService.setTimeStringFromPtLuxonDate(this.operationInstance.endTime);
      } else {
        this.operationInstance.stepCollectionInstance.stepInstances[position].endTimeToShow
          = this.dateService.setTimeStringFromPtLuxonDate(this.operationInstance.stepCollectionInstance.stepInstances[position].endTime);
      }
    }
  }

  confirm(type: DailyLogType, position?: number, end?: boolean) {

    switch (type) {
      case DailyLogType.OperationStart:
        if (this.canConfirmOperationTimes) {
          this.operationInstance.startConfirmed = true;
        }
        break;
      case DailyLogType.OperationEnd:
        if (this.canConfirmOperationTimes) {
          this.operationInstance.endConfirmed = true;
        }
        break;
      case DailyLogType.Step:

        if (position === null || position === undefined) {
          break;
        }
        if ((this.operationInstance.stepCollectionInstance.stepInstances[position]).canConfirmTimes) {
          if (!end) {
            this.operationInstance.stepCollectionInstance.stepInstances[position].startConfirmed = true;
          } else {
            this.operationInstance.stepCollectionInstance.stepInstances[position].endConfirmed = true;
          }
        }
        break;
    }

  }

  async save() {
    this.loading = true;
    this.animationPaused = true;

    if (!await this.validateResponsibilityForSave()) {
      this.loading = false;
      this.animationPaused = false;
      return;
    }

    await this.handleSplitSteps();

    if (this.operationInstance.projectSpecificOperation.operationSummary.classificationName === 'Delay') {
      const type = this.operationInstance.isInterruption
        ? OperationType.Interruption
        : OperationType.Delay;
      this.navCtrl.push(AffectedOperationsPage, {
        instance: this.operationInstance,
        type: type,
        dailyLogDate: this.dailyLogDate,
        originalInstanceCopy: this.originalInstanceCopy
      });
      this.loading = false;
      return;
    }

    if (!!this.saved) {
      this.loading = false;
      return;
    }

    const saveRequest: OperationInstanceSaveRequest = {
      operationInstanceEditor: cloneDeep(this.operationInstance) as DailyLogOperationInstance,
      dailyLogDate: cloneDeep(this.dailyLogDate.dateTime) as DateTime,
    }

    await this.operationSaveService.saveOperationInstance(saveRequest)
      .then(async savedInstance => {

        // todo we need to sync local app data here i think, with the updated saved operation/records data
        this.navHelperService.unsavedChanges = false;
        this.saved = true;
        this.navHelperService.warned = true;

        this.presentSaveToast().then(() => {
          this.navCtrl.setRoot(DailyLogPage);
          this.navHelperService.updateNavMenuPage({page: NavMainMenuPage});
        });
      }, async error => {
        this.loading = false;
        throw (error);
      });
  }

  regexChange(inputTime: string) {
    if (inputTime.charAt(0) === '2') {
      this.mask[1] = new RegExp('[0-3]');
    } else {
      this.mask[1] = new RegExp('\\d');
    }
  }

  presentSaveToast(): Promise<any> {
    const toast = this.toastCtrl.create({
      message:
        `${this.operationInstance.isConcurrentOperation ? 'Concurrent ' : 'Contractual '}`
        + 'Operation \''
        + `${this.operation.name ? this.operation.name : this.operation.operationSummary.name}`
        + '\' updated successfully',
      duration: 4500,
      position: 'bottom'
    });

    return toast.present();
  }

  setTypes() {

    if (!!this.operationInstance.startTime) {
      this.operationInstance.startConcurrent = this.timeType(this.operationInstance.startTime.getDate());
    }

    if (!!this.operationInstance.endTime) {
      this.operationInstance.endConcurrent = this.timeType(this.operationInstance.endTime.getDate());
    }

    const steps = !this.operationInstance.stepCollectionInstance
      ? []
      : this.operationInstance.stepCollectionInstance.stepInstances;

    steps.forEach(element => {
      element.isConcurrent = this.timeType(element.startTime.getDate());
    });

    this.badgesLoaded = true;
  }

  timeType(date: DateTime): boolean {

    if (!this.operationInstance.projectSpecificOperation) {
      return false;
    }

    let switchTimes = [];

    if (!date) {
      switchTimes = this.operationInstance.switchTimes;
    } else {
      switchTimes = this.operationInstance.switchTimes.filter(p => p.date.dateTime <= date);
    }

    if (switchTimes.length < 1) {
      return this.operationInstance.isConcurrentOperation;
    }

    let switchTime = switchTimes[0];

    switchTimes.forEach(time => {
      if (time.date.dateTime > switchTime.date.getDate()) {
        switchTime = time;
      }
    });

    return switchTime.isConcurrent;
  }


  async swapOperationClass() {
    if (!this.operationInstance || this.operationStartedBeforeDailyLogDate()) {
      return;
    }

    this.operationInstance.isConcurrentOperation = !this.operationInstance.isConcurrentOperation;

    this.swapMessage = this.operationInstance.isConcurrentOperation
      ? 'Change to contractual'
      : 'Change to concurrent';

    this.navHelperService.unsavedChanges = true;

    this.operationInstance.switchTimes.forEach(element => {
      this.stepService.splitStepsAtTime(this.operationInstance, element.date);
    });

    await this.stepService.attemptJoinStepsInOperation(this.operationInstance);

    this.setTypes();

    await this.validateOperation();
  }

  operationStartedBeforeDailyLogDate(): boolean {
    const operationStart = this.operationInstance && this.operationInstance.startTime
      ? this.operationInstance.startTime.dateTime
      : null;
    const dailyLogDate = this.dailyLogDate
      ? this.dailyLogDate.dateTime
      : null;

    if (!operationStart || !dailyLogDate) {
      return false;
    }

    return operationStart.toMillis() < dailyLogDate.toMillis();
  }

  getSwapOperationTitle(): string {
    const isConcurrent = this.operationInstance && this.operationInstance.startConcurrent;

    if (this.operationStartedBeforeDailyLogDate()) {
      return 'This operation started on a previous day. Use the \'Split operation\' (scissors button) instead to change this operation to' + (isConcurrent ? ' contractual.' : ' concurrent.');
    }

    return this.swapMessage;
  }

  setConfirmedFromLogs(instance: DailyLogOperationInstance) {
    if (!this.logs) {
      return;
    }

    const startLog = this.logs.find(p => p.typeId === DailyLogType.OperationStart);

    if (!!startLog) {
      instance.startConfirmed = startLog.startTimeConfirmed;
    }

    const endLog = this.logs.find(p => p.typeId === DailyLogType.OperationEnd);

    if (!!endLog) {
      instance.endConfirmed = endLog.endTimeConfirmed;
    }

    const stepLogs = this.logs.filter(p => p.typeId === DailyLogType.Step);

    if (!!stepLogs && !!this.operationInstance.stepCollectionInstance) {
      stepLogs.forEach(stepLog => {
        const step = this.operationInstance.stepCollectionInstance.stepInstances.find(p => p.id === stepLog.stepInstance.id);

        if (!step) {
          return;
        }

        step.startConfirmed = stepLog.startTimeConfirmed;
        step.endConfirmed = stepLog.endTimeConfirmed;
      });
    }
  }

  private canConfirmStepTime(stepInstance: StepInstance) {
    return this.canConfirmOperationTimes || !stepInstance.step.isSystemDefault;
  }

  private async refreshOperationValidationSettings(): Promise<void> {
    this.loading = true;
    this.operationValidationSettings = null;
    this.saveValidationIsValid = false;
    this.validationResult = new ValidationResult();
    try {
      this.operationValidationSettings = await this.operationService.getOperationValidationSettings(
        this.operationInstance, this.dailyLogDate);
      await this.validateOperation();
    } catch (error) {
      this.operationValidationSettings = null;
      this.saveValidationIsValid = false;
      await this.toastCtrl.create({
        message: 'Unable to load operation settings for this reporting day. Please reopen the operation to try again.',
        duration: 6000,
        position: 'bottom'
      }).present();
    } finally {
      this.loading = false;
    }
  }

  private async validateOperation(mostRecentTime?: PtLuxonDate) {
    if (!this.operationValidationSettings) {
      return;
    }
    await this.calculateOperationOffsets(this.operationInstance);
    this.validationResult = await this.operationValidatorService.validateOperation(
      this.operationInstance,
      mostRecentTime ? mostRecentTime : this.operationInstance.startTime, this.dailyLogDate, null, null);
    await this.checkOverlaps();
    await this.checkLockedDayDateRestrictions();
    this.responsibilityRequired = await this.operationService.isResponsibilityRequired(this.operationInstance, this.dailyLogDate, this.operationValidationSettings);
    this.saveValidationIsValid = this.validationResult.isValid;
    this.addResponsibilityValidationError();
    this.multipleDaysExist = await this.operationValidatorService.multipleDaysExist(this.operationInstance);
  }

  get responsibilityInvalid(): boolean {
    return this.responsibilityRequired && this.responsibilityValidationAttempted && !this.responsibilityIsValid();
  }

  responsibilityRequired: boolean = true;

  async responsibilityChanged() {
    this.navHelperService.unsavedChanges = true;

    await this.validateOperation();
  }

  private async validateResponsibilityForSave(): Promise<boolean> {
    if (!this.operationValidationSettings) {
      return false;
    }
    await this.validateOperation();
    this.responsibilityValidationAttempted = this.responsibilityRequired;
    return !this.responsibilityInvalid;
  }

  private addResponsibilityValidationError() {
    if (this.responsibilityRequired && !this.responsibilityIsValid()) {
      this.validationResult.addError(
        'Responsibility must be selected as either Client or Owner.',
        ErrorType.Error
      );
    }
  }

  private responsibilityIsValid(): boolean {
    return this.operationInstance.responsibilityTypeId === ResponsibilityType.Client
      || this.operationInstance.responsibilityTypeId === ResponsibilityType.Owner;
  }

  // needs to exist here instead of in operationValidatorService due to circular dependency issue
  private async checkOverlaps(): Promise<void> {
    if (!this.operationInstance) {
      return;
    }

    const instances = await this.operationService.getAllProjectSpecificOperationInstancesForDay(
      this.dailyLogDate,
      this.operationInstance.id
    );

    const operationInstanceCopy = cloneDeep(this.operationInstance);

    this.upsertCurrentOperation(instances, operationInstanceCopy);

    let overlaps = await this.dailyLogValidationService.findOverlapTimes(instances, this.dailyLogDate);
    overlaps = this.filterOverlapsForCurrentOperation(overlaps, operationInstanceCopy);

    for (const overlap of overlaps) {
      await this.prepareOverlapForDisplay(overlap, operationInstanceCopy);
      await this.addOverlapWarning(overlap);
    }

    await this.calculateOperationOffsets(this.operationInstance);
  }

  private upsertCurrentOperation(instances: any[], operationInstanceCopy: any): void {
    const index = instances.findIndex(instance => this.isSameOperation(instance, operationInstanceCopy));

    if (index !== -1) {
      instances[index] = operationInstanceCopy;
      return;
    }

    instances.push(operationInstanceCopy);
  }

  private filterOverlapsForCurrentOperation(overlaps: any[], operationInstanceCopy: any): any[] {
    return overlaps.filter(overlap => {
      if (!overlap?.previousOperation) {
        return false;
      }

      return this.isSameOperation(overlap.previousOperation, operationInstanceCopy);
    });
  }

  private async prepareOverlapForDisplay(overlap: any, operationInstanceCopy: any): Promise<void> {
    overlap.type = ActionType.OperationOverlap;

    if (overlap.previousOperation) {
      this.applyDisplayTimes(overlap.previousOperation);

      if (this.isSameOperation(overlap.previousOperation, operationInstanceCopy)) {
        overlap.previousOperation = operationInstanceCopy;
      }
    }

    if (overlap.operationInstance) {
      this.applyDisplayTimes(overlap.operationInstance);

      // findOverlapTimes mutates / overwrites the current operation somewhere,
      // so replace it again with the in-edit copy when it matches
      if (this.isSameOperation(overlap.operationInstance, operationInstanceCopy)) {
        overlap.operationInstance = operationInstanceCopy;
      }
    }
  }

  private applyDisplayTimes(operation: any): void {
    operation.startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(operation.startTime);
    operation.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(operation.endTime);
  }

  private async addOverlapWarning(overlap: any): Promise<void> {
    this.validationResult.addError(
      await this.dailyLogValidationService.buildStandardOverlapMessage(
        overlap, this.dailyLogDate.dateTime, 'Saving this operation will cause an overlap between', 'End of Day'),
      ErrorType.Warning,
      null,
      overlap
    );
  }

  private isSameOperation(left: any, right: any): boolean {
    if (!left || !right) {
      return false;
    }

    if (left.id != null && right.id != null) {
      return left.id === right.id;
    }

    return left.localId != null && right.localId != null && left.localId === right.localId;
  }

  private getOperationLabel(operation: any, fallback: string): string {
    if (!operation) {
      return fallback;
    }

    return (
      operation.projectSpecificOperation?.name ||
      operation.projectSpecificOperation?.operationSummary?.name ||
      fallback
    );
  }

  // when operation page is loaded we set some flags for each operation time depending on if the times are on the next day
  // now if any times that were not already on the next day, are now moved onto the next day, and the user does not have permission to do so
  // when must stop them from being able to save
  private async checkLockedDayDateRestrictions() {
    if (this.config.isImplementerEnvironment && !this.lockedDayPermissions.functionalPermissions.canImpactFutureDay) {
      if (this.operationInstance.startTime && this.operationInstance.startTime.dateTime) {
        if (!this.operationInstance.startDisabledForLockedDay && this.operationInstance.startTime.dateTime >= this.endOfDay.dateTime) {
          this.validationResult.addError(`Operation Start time may not be moved onto the next day.`, ErrorType.Error);
        }
      }

      if (this.operationInstance.stepCollectionInstance && this.operationInstance.stepCollectionInstance.stepInstances) {
        for (let i = 0; i < this.operationInstance.stepCollectionInstance.stepInstances.length; i++) {
          const stepInstance = this.operationInstance.stepCollectionInstance.stepInstances[i];

          if (!stepInstance.startDisabledForLockedDay && stepInstance.startTime && stepInstance.startTime.dateTime >= this.endOfDay.dateTime) {
            this.validationResult.addError(`Step <strong>${stepInstance.step.name}</strong> Start time may not be on the next day.`, ErrorType.Error);
          }

          if (stepInstance.secondsSpan !== 0 && !stepInstance.endDisabledForLockedDay && stepInstance.endTime && stepInstance.endTime.dateTime >= this.endOfDay.dateTime) {
            this.validationResult.addError(`Step <strong>${stepInstance.step.name}</strong> End time may not be moved onto the next day.`, ErrorType.Error);
          }
        }
      }

      if (this.operationInstance.endTimeToShow && this.operationInstance.endTime.dateTime) {
        if (!this.operationInstance.endDisabledForLockedDay && this.operationInstance.endTime.dateTime >= this.endOfDay.dateTime) {
          this.validationResult.addError(`You do not have the correct permissions to move Operation End time onto the next day.`, ErrorType.Error);
        }
      }
    }
  }

  async calculateOperationOffsets(instance: DailyLogOperationInstance) {
    if (!instance) {
      return;
    }

    instance.startTimeOffset = instance.startTime.dateTime && instance.startTime.offsetNotNull()
      ? instance.startTime.dateTime.offset / 60
      : NaN;

    if (!!instance.stepCollectionInstance) {
      for (const stepInstance of instance.stepCollectionInstance.stepInstances) {
        if (!!stepInstance.startTime) {
          stepInstance.startTimeOffset = stepInstance.startTime.dateTime && stepInstance.startTime.offsetNotNull()
            ? stepInstance.startTime.dateTime.offset / 60
            : NaN;
        }

        if (!!stepInstance.endTime) {
          stepInstance.endTimeOffset = stepInstance.endTime.dateTime && stepInstance.endTime.offsetNotNull()
            ? stepInstance.endTime.dateTime.offset / 60
            : NaN;
        }
      }
    }

    instance.endTimeOffset = instance.endTime.dateTime && instance.endTime.offsetNotNull()
      ? instance.endTime.dateTime.offset / 60
      : NaN;
  }

  async offsetSelected(ptLuxonDate: any, startChanged: boolean, position: number, stepInstance?: StepInstance) {
    // this method will be called when offset is nulled by startChanged()/endChanged(), so just return here if so
    if (ptLuxonDate.offsetNull()) {
      return;
    }

    // instance start time offset changed
    if (startChanged && !stepInstance) {
      await this.instanceStartOffsetChanged(ptLuxonDate);
      await this.validateOperation();

      return;
    }

    // instance end time offset changed
    if (!startChanged && !stepInstance) {
      await this.instanceEndOffsetChanged(ptLuxonDate);
      await this.validateOperation();

      return;
    }

    // steps
    if (stepInstance) {
      if (startChanged) {
        await this.stepStartOffsetChanged(ptLuxonDate, stepInstance, position);
        await this.validateOperation();
      } else {
        await this.stepEndOffsetChanged(ptLuxonDate, stepInstance, position);
        await this.validateOperation();
      }
    }
  }

  async instanceStartOffsetChanged(ptLuxonDate: PtLuxonDate) {
    this.operationInstance.startTimeOffset = ptLuxonDate.dateTime.offset / 60;
  }

  async instanceEndOffsetChanged(ptLuxonDate: PtLuxonDate) {
    this.operationInstance.endTimeOffset = ptLuxonDate.dateTime.offset / 60;
  }

  async stepStartOffsetChanged(ptLuxonDate: PtLuxonDate, stepInstance: StepInstance, position: number) {
    this.operationInstance.stepCollectionInstance.stepInstances[position].startTimeOffset = ptLuxonDate.dateTime.offset / 60;

    // if step is point in time step
    if (stepInstance.step.defaultSecondsSpan === 0) {
      this.operationInstance.stepCollectionInstance.stepInstances[position].endTime = this.operationInstance.stepCollectionInstance.stepInstances[position].startTime;
      this.operationInstance.stepCollectionInstance.stepInstances[position].endTimeOffset
        = this.operationInstance.stepCollectionInstance.stepInstances[position].startTimeOffset;
      this.operationInstance.stepCollectionInstance.stepInstances[position].endTime.multipleOffsets
        = this.operationInstance.stepCollectionInstance.stepInstances[position].startTime.multipleOffsets;
      this.operationInstance.stepCollectionInstance.stepInstances[position].endTimeToShow
        = this.operationInstance.stepCollectionInstance.stepInstances[position].startTimeToShow;
    }
  }

  async stepEndOffsetChanged(ptLuxonDate: PtLuxonDate, stepInstance: StepInstance, position: number) {
    this.operationInstance.stepCollectionInstance.stepInstances[position].endTimeOffset = ptLuxonDate.dateTime.offset / 60;
  }

  private async handleSplitSteps() {
    // if a step crosses midnight (so goes into the next day) - it will need to be split
    const midnight = await this.dateService.getEndOfDayLocally(this.dailyLogDate.dateTime);

    await this.stepService.attemptJoinStepsInOperation(this.operationInstance);

    await this.stepService.splitStepsAtTime(this.operationInstance, midnight, true);

    for (let i = 0; i < this.operationInstance.switchTimes.length; i++) {
      await this.stepService.splitStepsAtTime(this.operationInstance, this.operationInstance.switchTimes[i].date);
    }

    // need to ensure types are up to date after any potentially splitting
    this.setTypes();
  }

  private setSupportContext() {
    this.supportContextService.setComponentContext(
      SupportContextKey.EditOperation,
      'Edit Operation',
      'OperationDetailsEditPage',
      this.getOperationSupportDetails()
    );
  }

  private getOperationSupportDetails(): {[key: string]: string} {
    return {
      instanceId: this.getOperationInstanceId(this.operationInstance),
      operationName: this.getOperationLabel(this.operationInstance, '')
    };
  }

  private getOperationInstanceId(operation: DailyLogOperationInstance): string {
    if (!operation) {
      return '';
    }

    return operation.id != null
      ? `${operation.id}`
      : operation.localId != null
        ? `${operation.localId}`
        : '';
  }
}



// WEBPACK FOOTER //
// ./src/pages/operations/operation-details-edit/operation-details-edit.ts